import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { localize2WithPath, localizeWithPath } from 'vscode/vscode/vs/nls';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { Categories } from 'vscode/vscode/vs/platform/action/common/actionCommonCategories';
import { registerAction2, Action2 } from 'vscode/vscode/vs/platform/actions/common/actions';
import { SetLogLevelAction } from './logsActions.js';
import { Extensions as Extensions$1 } from 'vscode/vscode/vs/workbench/common/contributions';
import { whenProviderRegistered } from 'vscode/vscode/vs/platform/files/common/files';
import { IFileService } from 'vscode/vscode/vs/platform/files/common/files.service';
import { Extensions } from 'vscode/vscode/vs/workbench/services/output/common/output';
import { IOutputService } from 'vscode/vscode/vs/workbench/services/output/common/output.service';
import { Disposable, DisposableMap, DisposableStore, toDisposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { CONTEXT_LOG_LEVEL, LogLevelToString, isLogLevel } from 'vscode/vscode/vs/platform/log/common/log';
import { ILogService, ILoggerService } from 'vscode/vscode/vs/platform/log/common/log.service';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { Event } from 'vscode/vscode/vs/base/common/event';
import { showWindowLogActionId, windowLogId } from 'vscode/vscode/vs/workbench/services/log/common/logConstants';
import { createCancelablePromise, timeout } from 'vscode/vscode/vs/base/common/async';
import { isCancellationError, getErrorMessage, CancellationError } from 'vscode/vscode/vs/base/common/errors';
import { IDefaultLogLevelsService } from 'vscode/vscode/vs/workbench/contrib/logs/common/defaultLogLevels.service';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { CounterSet } from 'vscode/vscode/vs/base/common/map';
import { IUriIdentityService } from 'vscode/vscode/vs/platform/uriIdentity/common/uriIdentity.service';
import { Schemas } from 'vscode/vscode/vs/base/common/network';

const _moduleId = "vs/workbench/contrib/logs/common/logs.contribution";
registerAction2(class extends Action2 {
    constructor() {
        super({
            id: SetLogLevelAction.ID,
            title: SetLogLevelAction.TITLE,
            category: Categories.Developer,
            f1: true
        });
    }
    run(servicesAccessor) {
        return servicesAccessor.get(IInstantiationService).createInstance(SetLogLevelAction, SetLogLevelAction.ID, SetLogLevelAction.TITLE.value).run();
    }
});
registerAction2(class extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.setDefaultLogLevel',
            title: ( localize2WithPath(_moduleId, 0, "Set Default Log Level")),
            category: Categories.Developer,
        });
    }
    run(servicesAccessor, logLevel, extensionId) {
        return servicesAccessor.get(IDefaultLogLevelsService).setDefaultLogLevel(logLevel, extensionId);
    }
});
let LogOutputChannels = class LogOutputChannels extends Disposable {
    constructor(logService, loggerService, contextKeyService, fileService, uriIdentityService) {
        super();
        this.logService = logService;
        this.loggerService = loggerService;
        this.contextKeyService = contextKeyService;
        this.fileService = fileService;
        this.uriIdentityService = uriIdentityService;
        this.contextKeys = ( (new CounterSet()));
        this.outputChannelRegistry = ( (Registry.as(Extensions.OutputChannels)));
        this.loggerDisposables = this._register(( (new DisposableMap())));
        const contextKey = CONTEXT_LOG_LEVEL.bindTo(contextKeyService);
        contextKey.set(LogLevelToString(loggerService.getLogLevel()));
        loggerService.onDidChangeLogLevel(e => {
            if (isLogLevel(e)) {
                contextKey.set(LogLevelToString(loggerService.getLogLevel()));
            }
        });
        this.onDidAddLoggers(loggerService.getRegisteredLoggers());
        this._register(loggerService.onDidChangeLoggers(({ added, removed }) => {
            this.onDidAddLoggers(added);
            this.onDidRemoveLoggers(removed);
        }));
        this._register(loggerService.onDidChangeVisibility(([resource, visibility]) => {
            const logger = loggerService.getRegisteredLogger(resource);
            if (logger) {
                if (visibility) {
                    this.registerLogChannel(logger);
                }
                else {
                    this.deregisterLogChannel(logger);
                }
            }
        }));
        this.registerShowWindowLogAction();
        this._register(Event.filter(contextKeyService.onDidChangeContext, e => e.affectsSome(this.contextKeys))(() => this.onDidChangeContext()));
    }
    onDidAddLoggers(loggers) {
        for (const logger of loggers) {
            if (logger.when) {
                const contextKeyExpr = ContextKeyExpr.deserialize(logger.when);
                if (contextKeyExpr) {
                    for (const key of ( (contextKeyExpr.keys()))) {
                        this.contextKeys.add(key);
                    }
                    if (!this.contextKeyService.contextMatchesRules(contextKeyExpr)) {
                        continue;
                    }
                }
            }
            if (logger.hidden) {
                continue;
            }
            this.registerLogChannel(logger);
        }
    }
    onDidChangeContext() {
        for (const logger of this.loggerService.getRegisteredLoggers()) {
            if (logger.when) {
                if (this.contextKeyService.contextMatchesRules(ContextKeyExpr.deserialize(logger.when))) {
                    this.registerLogChannel(logger);
                }
                else {
                    this.deregisterLogChannel(logger);
                }
            }
        }
    }
    onDidRemoveLoggers(loggers) {
        for (const logger of loggers) {
            if (logger.when) {
                const contextKeyExpr = ContextKeyExpr.deserialize(logger.when);
                if (contextKeyExpr) {
                    for (const key of ( (contextKeyExpr.keys()))) {
                        this.contextKeys.delete(key);
                    }
                }
            }
            this.deregisterLogChannel(logger);
        }
    }
    registerLogChannel(logger) {
        const channel = this.outputChannelRegistry.getChannel(logger.id);
        if (channel && this.uriIdentityService.extUri.isEqual(channel.file, logger.resource)) {
            return;
        }
        const disposables = ( (new DisposableStore()));
        const promise = createCancelablePromise(async (token) => {
            await whenProviderRegistered(logger.resource, this.fileService);
            try {
                await this.whenFileExists(logger.resource, 1, token);
                const existingChannel = this.outputChannelRegistry.getChannel(logger.id);
                const remoteLogger = existingChannel?.file?.scheme === Schemas.vscodeRemote ? this.loggerService.getRegisteredLogger(existingChannel.file) : undefined;
                if (remoteLogger) {
                    this.deregisterLogChannel(remoteLogger);
                }
                const hasToAppendRemote = existingChannel && logger.resource.scheme === Schemas.vscodeRemote;
                const id = hasToAppendRemote ? `${logger.id}.remote` : logger.id;
                const label = hasToAppendRemote ? ( localizeWithPath(_moduleId, 1, "{0} (Remote)", logger.name ?? logger.id)) : logger.name ?? logger.id;
                this.outputChannelRegistry.registerChannel({ id, label, file: logger.resource, log: true, extensionId: logger.extensionId });
                disposables.add(toDisposable(() => this.outputChannelRegistry.removeChannel(id)));
                if (remoteLogger) {
                    this.registerLogChannel(remoteLogger);
                }
            }
            catch (error) {
                if (!isCancellationError(error)) {
                    this.logService.error('Error while registering log channel', ( (logger.resource.toString())), getErrorMessage(error));
                }
            }
        });
        disposables.add(toDisposable(() => promise.cancel()));
        this.loggerDisposables.set(( (logger.resource.toString())), disposables);
    }
    deregisterLogChannel(logger) {
        this.loggerDisposables.deleteAndDispose(( (logger.resource.toString())));
    }
    async whenFileExists(file, trial, token) {
        const exists = await this.fileService.exists(file);
        if (exists) {
            return;
        }
        if (token.isCancellationRequested) {
            throw ( (new CancellationError()));
        }
        if (trial > 10) {
            throw ( (new Error(`Timed out while waiting for file to be created`)));
        }
        this.logService.debug(`[Registering Log Channel] File does not exist. Waiting for 1s to retry.`, ( (file.toString())));
        await timeout(1000, token);
        await this.whenFileExists(file, trial + 1, token);
    }
    registerShowWindowLogAction() {
        this._register(registerAction2(class ShowWindowLogAction extends Action2 {
            constructor() {
                super({
                    id: showWindowLogActionId,
                    title: ( localize2WithPath(_moduleId, 2, "Show Window Log")),
                    category: Categories.Developer,
                    f1: true
                });
            }
            async run(servicesAccessor) {
                const outputService = servicesAccessor.get(IOutputService);
                outputService.showChannel(windowLogId);
            }
        }));
    }
};
LogOutputChannels = ( (__decorate([
    ( (__param(0, ILogService))),
    ( (__param(1, ILoggerService))),
    ( (__param(2, IContextKeyService))),
    ( (__param(3, IFileService))),
    ( (__param(4, IUriIdentityService)))
], LogOutputChannels)));
let LogLevelMigration = class LogLevelMigration {
    constructor(defaultLogLevelsService) {
        defaultLogLevelsService.migrateLogLevels();
    }
};
LogLevelMigration = ( (__decorate([
    ( (__param(0, IDefaultLogLevelsService)))
], LogLevelMigration)));
( (Registry.as(Extensions$1.Workbench))).registerWorkbenchContribution(LogOutputChannels, 3 );
( (Registry.as(Extensions$1.Workbench))).registerWorkbenchContribution(LogLevelMigration, 4 );
